import { baseApi } from './baseApi'

import { BillingAttachmentDto } from 'store/types'

// What an attachment hangs off. A charge carries internal documents; a payment
// carries the paper EOB behind a hand-entered adjudication, which the next
// payer's COB claim needs. The two differ only in the path.
export type AttachmentOwner = { type: 'charge' | 'payment'; id: string }

const ownerPath = (owner: AttachmentOwner) => {
  // A missing id would otherwise be spelled into the URL and come back as a
  // "not found" from the API, which reads as a data problem rather than the
  // caller bug it is.
  if (!owner?.id) {
    throw new Error(`Cannot address attachments: no ${owner?.type || 'owner'} id`)
  }

  return owner.type === 'payment' ? `payments/${owner.id}` : `charges/${owner.id}`
}

type OwnerScoped = {
  owner: AttachmentOwner
}

type RecordRequest = OwnerScoped & {
  attachmentId: string
  fileName: string
  contentType?: string
}

type AttachmentScoped = OwnerScoped & {
  attachmentId: string
}

export const billingAttachmentsApi = baseApi.injectEndpoints({
  // These endpoints changed shape (charge-scoped -> owner-scoped). Injection
  // keeps the first definition it saw unless told otherwise, so without this a
  // hot-reloaded session keeps calling the previous signatures and addresses
  // `/charges/undefined/...` with arguments that no longer carry a charge id.
  overrideExisting: true,
  endpoints: (builder) => ({
    getBillingAttachments: builder.query<BillingAttachmentDto[], OwnerScoped>({
      query: ({ owner }) => ({
        url: `/lab/billing/${ownerPath(owner)}/attachments`,
      }),
      providesTags: ['BillingAttachments'],
    }),
    getAttachmentUploadUrl: builder.mutation<
      { attachmentId: string; url: string },
      OwnerScoped & { fileName: string }
    >({
      query: ({ owner, fileName }) => ({
        url: `/lab/billing/${ownerPath(owner)}/attachments/upload-url`,
        params: { fileName },
      }),
    }),
    recordBillingAttachment: builder.mutation<BillingAttachmentDto, RecordRequest>({
      query: ({ owner, ...body }) => ({
        url: `/lab/billing/${ownerPath(owner)}/attachments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BillingAttachments', 'BillingCharges'],
    }),
    deleteBillingAttachment: builder.mutation<void, AttachmentScoped>({
      query: ({ owner, attachmentId }) => ({
        url: `/lab/billing/${ownerPath(owner)}/attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BillingAttachments', 'BillingCharges'],
    }),
    getBillingAttachmentUrl: builder.mutation<
      { url: string; fileName: string },
      AttachmentScoped & { preview?: boolean }
    >({
      query: ({ owner, attachmentId, preview }) => ({
        url: `/lab/billing/${ownerPath(owner)}/attachments/${attachmentId}/url`,
        params: preview ? { preview: 'true' } : undefined,
      }),
    }),
  }),
})

export const {
  useGetBillingAttachmentsQuery,
  useGetAttachmentUploadUrlMutation,
  useRecordBillingAttachmentMutation,
  useDeleteBillingAttachmentMutation,
  useGetBillingAttachmentUrlMutation,
} = billingAttachmentsApi
